import { useCallback } from 'react';
import {
  CentrifugalMessagesConfig,
  MESSAGE_STATUS,
  useCentrifuge,
  useMessageContext
} from 'react-easyrocket';
import { useFreeSpins } from './useFreeSpins';
import { useModalContext } from './useModalContext';
import { useBonusModalStore } from 'src/store';
import { useBonusModalData } from 'src/components/Modal/BonusModal/hooks/useBonusModalData';
import { getShouldReplace } from 'src/utils/getShouldReplace';

type MessageStatus = 'info' | 'success' | 'failure';

interface UserBonusMessage {
  type: 'USER_BONUS';
  data?: {
    amount: number;
    currency: string;
    type: number;
    id: number;
  };
}

interface UserMessage {
  type: 'USER_MESSAGE';
  data?: {
    messageType: 'info' | 'success' | 'failure';
    text: string;
  };
}

interface UserFreeSpins {
  type: 'USER_FREESPINS';
  data: {
    number: number;
    betAmount: number;
    dayLimit: number;
    numberDay: number;
  };
}

const getMessageStatus = (status: MessageStatus): MESSAGE_STATUS => {
  if (status === 'failure') return MESSAGE_STATUS.ERROR;
  if (status === 'success') return MESSAGE_STATUS.SUCCESS;
  return MESSAGE_STATUS.INFO;
};

export const useCentrifugeMessages = () => {
  const { openMessage } = useMessageContext();
  const { saveFreeSpins } = useFreeSpins();
  const { openModal } = useModalContext();
  const { idBonusModal, setModal } = useBonusModalStore();
  const getBonusInfo = useBonusModalData();

  const handleUserMessage = useCallback(
    ({ data }: UserMessage) => {
      try {
        const { messageType: status, text } = data || {};
        if (!status || !text) return;
        openMessage?.(`${text}`, getMessageStatus(status));
      } catch {
        console.error('Failed to handle USER_MESSAGE');
      }
    },
    [openMessage]
  );

  const handleUserBonus = useCallback(
    async (data: UserBonusMessage) => {
      const incomingId = data?.data?.id;

      if (!incomingId) return;
      if (idBonusModal === incomingId) return;
      const bonusData = await getBonusInfo(incomingId);
      if (!bonusData) return;
      setModal(incomingId);
      if (!bonusData) return;

      openModal('BONUS', {
        props: {
          bonusData
        },
        replace: getShouldReplace()
      });
    },
    [idBonusModal, setModal, openModal]
  );

  const handleUserFreespins = useCallback(
    ({ data }: UserFreeSpins) => {
      if (!data) return;
      saveFreeSpins?.(data);
    },
    [saveFreeSpins]
  );

  const messagesConfig: CentrifugalMessagesConfig = {
    USER_MESSAGE: handleUserMessage,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO - fix type of USER_BONUS message in lib
    USER_BONUS: handleUserBonus,
    USER_FREESPINS: handleUserFreespins
  };

  useCentrifuge({ messagesConfig });
};
